import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  rejectMessage,
  wireInviteCustomerUserData,
  type ApiCustomerUser,
  type AssociateCustomerLocationInput,
  type CustomerContactCreateResponse,
  type CustomerContactDeleteResponse,
  type CustomerContactDetailResponse,
  type CustomerContactMutationInput,
  type CustomerContactRestoreResponse,
  type CustomerContactRolesResponse,
  type CustomerContactUpdateResponse,
  type CustomerContactsListResponse,
  type CustomerLocationDeleteResponse,
  type CustomerLocationMutationResponse,
  type CustomerLocationsListResponse,
  type CustomerOverviewResponse,
  type CustomerStatusMutationResponse,
  type CustomerWireStatus,
  type InviteCustomerUserInput,
  type UpdateCustomerLocationInput,
  type UploadCustomerLogoResponse,
} from "api-client";
import { customersService } from "../../services/customers.service";
import { customerContactsService } from "../../services/customer-contacts.service";
import { customerLocationsService } from "../../services/customer-locations.service";
import { OPERATOR_SCOPED_ID } from "../../constants/operator.constants";
import type { CustomerDetailState } from "./customer-detail.models";

type CustomerDetailRootSlice = { customerDetail: CustomerDetailState };

function resolveContactsOperatorId(state: CustomerDetailState): number {
  const fromCustomer = state.customer?.operatorId;
  if (typeof fromCustomer === "number" && Number.isFinite(fromCustomer) && fromCustomer > 0) {
    return fromCustomer;
  }
  return OPERATOR_SCOPED_ID;
}

function resolveContactsCustomerId(state: CustomerDetailState): string | null {
  if (state.selectedCustomerId?.trim()) {
    return state.selectedCustomerId.trim();
  }
  if (state.customer?.customerId != null) {
    return String(state.customer.customerId);
  }
  return null;
}

export const fetchCustomerOverview = createAsyncThunk<
  CustomerOverviewResponse,
  { customerId: string; operatorId?: string | number },
  { state: CustomerDetailRootSlice; rejectValue: string }
>("customerDetail/fetchOverview", async ({ customerId, operatorId }, { rejectWithValue }) => {
  try {
    return await customersService.getCustomerOverview(customerId, operatorId);
  } catch (e: unknown) {
    return rejectWithValue(rejectMessage(e, "Failed to fetch customer overview"));
  }
});

export const updateCustomerStatus = createAsyncThunk<
  CustomerStatusMutationResponse,
  { customerId: string; operatorId: string | number; status: CustomerWireStatus },
  { state: CustomerDetailRootSlice; rejectValue: string }
>("customerDetail/updateStatus", async ({ customerId, operatorId, status }, { rejectWithValue }) => {
  try {
    return await customersService.updateCustomerStatus(customerId, operatorId, { status });
  } catch (e: unknown) {
    return rejectWithValue(rejectMessage(e, "Failed to update customer status"));
  }
});

export const deleteCustomer = createAsyncThunk<
  void,
  { customerId: number; operatorId: number },
  { state: CustomerDetailRootSlice; rejectValue: string }
>("customerDetail/deleteCustomer", async ({ customerId, operatorId }, { rejectWithValue }) => {
  try {
    await customersService.deleteCustomer({ customerId, operatorId });
  } catch (e: unknown) {
    return rejectWithValue(rejectMessage(e, "Could not delete customer."));
  }
});

export const fetchCustomerLocations = createAsyncThunk<
  CustomerLocationsListResponse,
  { customerId: string; search?: string },
  { state: CustomerDetailRootSlice; rejectValue: string }
>("customerDetail/fetchLocations", async ({ customerId, search }, { rejectWithValue }) => {
  try {
    const term = search?.trim() ?? "";
    return await customerLocationsService.getCustomerLocations(
      customerId,
      term ? { search: term } : undefined,
    );
  } catch (e: unknown) {
    return rejectWithValue(rejectMessage(e, "Failed to fetch customer locations"));
  }
});

export const associateCustomerLocation = createAsyncThunk<
  CustomerLocationMutationResponse,
  { customerId: string; body: AssociateCustomerLocationInput },
  { state: CustomerDetailRootSlice; rejectValue: string }
>("customerDetail/associateLocation", async ({ customerId, body }, { rejectWithValue }) => {
  try {
    return await customerLocationsService.associateCustomerLocation(customerId, body);
  } catch (e: unknown) {
    return rejectWithValue(rejectMessage(e, "Failed to associate location"));
  }
});

export const updateCustomerLocation = createAsyncThunk<
  CustomerLocationMutationResponse,
  { customerId: string; locationId: number; body: UpdateCustomerLocationInput },
  { state: CustomerDetailRootSlice; rejectValue: string }
>("customerDetail/updateLocation", async ({ customerId, locationId, body }, { rejectWithValue }) => {
  try {
    return await customerLocationsService.updateCustomerLocation(customerId, locationId, body);
  } catch (e: unknown) {
    return rejectWithValue(rejectMessage(e, "Failed to update location"));
  }
});

export const deleteCustomerLocation = createAsyncThunk<
  CustomerLocationDeleteResponse,
  { customerId: string; locationId: number },
  { state: CustomerDetailRootSlice; rejectValue: string }
>("customerDetail/deleteLocation", async ({ customerId, locationId }, { rejectWithValue }) => {
  try {
    return await customerLocationsService.deleteCustomerLocation(customerId, locationId);
  } catch (e: unknown) {
    return rejectWithValue(rejectMessage(e, "Failed to remove location"));
  }
});

export const inviteCustomerUser = createAsyncThunk<
  ApiCustomerUser,
  { customerId: string; operatorId: string | number; body: InviteCustomerUserInput },
  { state: CustomerDetailRootSlice; rejectValue: string }
>("customerDetail/inviteUser", async ({ customerId, operatorId, body }, { rejectWithValue }) => {
  try {
    const response = await customersService.inviteCustomerUser(customerId, operatorId, body);
    return wireInviteCustomerUserData(response.data);
  } catch (e: unknown) {
    return rejectWithValue(rejectMessage(e, "Failed to invite user"));
  }
});

export const uploadCustomerLogo = createAsyncThunk<
  UploadCustomerLogoResponse,
  { customerId: string; operatorId: number; file: File },
  { state: CustomerDetailRootSlice; rejectValue: string }
>("customerDetail/uploadLogo", async ({ customerId, operatorId, file }, { rejectWithValue }) => {
  try {
    return await customersService.uploadCustomerLogo(customerId, operatorId, file);
  } catch (e: unknown) {
    return rejectWithValue(rejectMessage(e, "Failed to upload logo"));
  }
});

export const fetchCustomerContactRoles = createAsyncThunk<
  CustomerContactRolesResponse,
  void,
  { state: CustomerDetailRootSlice; rejectValue: string }
>("customerDetail/fetchContactRoles", async (_, { rejectWithValue }) => {
  try {
    return await customerContactsService.getContactRoles();
  } catch (e: unknown) {
    return rejectWithValue(rejectMessage(e, "Failed to fetch contact roles"));
  }
});

export const fetchCustomerContacts = createAsyncThunk<
  CustomerContactsListResponse,
  void,
  { state: CustomerDetailRootSlice; rejectValue: string }
>("customerDetail/fetchContacts", async (_, { getState, rejectWithValue }) => {
  const detail = getState().customerDetail;
  const customerId = resolveContactsCustomerId(detail);
  if (!customerId) {
    return rejectWithValue("Customer id is required");
  }

  const query = detail.contactsListQuery;
  try {
    return await customerContactsService.getCustomerContacts(customerId, {
      operatorId: resolveContactsOperatorId(detail),
      search: query.search.trim() || undefined,
      roleIds: query.roleCodes.length > 0 ? query.roleCodes : undefined,
      fromIndex: query.fromIndex,
      toIndex: query.toIndex,
      sortingField: "name",
      sortingOrder: query.sortDescending ? "desc" : "asc",
    });
  } catch (e: unknown) {
    return rejectWithValue(rejectMessage(e, "Failed to fetch customer contacts"));
  }
});

export const fetchCustomerContact = createAsyncThunk<
  CustomerContactDetailResponse,
  { customerId: string; contactId: number; operatorId: number },
  { state: CustomerDetailRootSlice; rejectValue: string }
>("customerDetail/fetchContact", async ({ customerId, contactId, operatorId }, { rejectWithValue }) => {
  try {
    return await customerContactsService.getCustomerContact(customerId, contactId, operatorId);
  } catch (e: unknown) {
    return rejectWithValue(rejectMessage(e, "Failed to fetch contact"));
  }
});

export const deleteCustomerContact = createAsyncThunk<
  CustomerContactDeleteResponse,
  { customerId: string; contactId: number; operatorId?: number | null },
  { state: CustomerDetailRootSlice; rejectValue: string }
>("customerDetail/deleteContact", async ({ customerId, contactId, operatorId }, { rejectWithValue }) => {
  try {
    return await customerContactsService.deleteCustomerContact(customerId, contactId, operatorId);
  } catch (e: unknown) {
    return rejectWithValue(rejectMessage(e, "Failed to delete contact"));
  }
});

export const restoreCustomerContact = createAsyncThunk<
  CustomerContactRestoreResponse,
  { customerId: string; contactId: number; operatorId?: number | null },
  { state: CustomerDetailRootSlice; rejectValue: string }
>("customerDetail/restoreContact", async ({ customerId, contactId, operatorId }, { rejectWithValue }) => {
  try {
    return await customerContactsService.restoreCustomerContact(customerId, contactId, operatorId);
  } catch (e: unknown) {
    return rejectWithValue(rejectMessage(e, "Failed to restore contact"));
  }
});

export const createCustomerContact = createAsyncThunk<
  CustomerContactCreateResponse,
  { customerId: string; operatorId: number; body: CustomerContactMutationInput },
  { state: CustomerDetailRootSlice; rejectValue: string }
>("customerDetail/createContact", async ({ customerId, operatorId, body }, { rejectWithValue }) => {
  try {
    return await customerContactsService.createCustomerContact(customerId, operatorId, body);
  } catch (e: unknown) {
    return rejectWithValue(rejectMessage(e, "Failed to create contact"));
  }
});

export const updateCustomerContact = createAsyncThunk<
  CustomerContactUpdateResponse,
  { customerId: string; contactId: number; operatorId: number; body: CustomerContactMutationInput },
  { state: CustomerDetailRootSlice; rejectValue: string }
>("customerDetail/updateContact", async ({ customerId, contactId, operatorId, body }, { rejectWithValue }) => {
  try {
    return await customerContactsService.updateCustomerContact(customerId, contactId, operatorId, body);
  } catch (e: unknown) {
    return rejectWithValue(rejectMessage(e, "Failed to update contact"));
  }
});
